import { useState } from "react";
import { useLocation, Redirect } from "react-router-dom";

import { Grid, Typography, Button, CircularProgress } from "@mui/material";

import { Select } from "@aclymatepackages/atoms";

import AccountAccessLayout from "./layouts/AccountAccessLayout";
import { ROLE_OPTIONS } from "../modules/UserManagementPanel";

import { useAuth } from "../../helpers/firebase";
import { fetchOurApi } from "../../helpers/utils/apiCalls";

const SuccessView = ({ action }) => {
  const message =
    action === "approve"
      ? "Access approved. The user will receive an email with login instructions."
      : "Request declined. The requesting buyer will be notified with your contact information.";

  return (
    <AccountAccessLayout title="Request Processed">
      <Typography variant="body1" align="center">
        {message}
      </Typography>
    </AccountAccessLayout>
  );
};

const JoinRequest = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const vendorId = params.get("vendorId");
  const requestId = params.get("requestId");
  const action = params.get("action");

  const [user] = useAuth();
  const { uid } = user || {};

  const [selectedRole, setSelectedRole] = useState("viewer");
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState("");

  const onConfirm = async () => {
    setIsLoading(true);
    setError("");
    try {
      await fetchOurApi({
        path: "/vendors/handle-vendor-join-request",
        method: "POST",
        data: {
          vendorId,
          requestId,
          action,
          adminUid: uid,
          ...(action === "approve" ? { role: selectedRole } : {})
        },
        callback: (res) => {
          if (res?.error) {
            setError(res.message || "An error occurred. Please try again.");
            return;
          }
          setIsComplete(true);
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <Redirect
        to={`/explorer?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}
      />
    );
  }

  if (isComplete) {
    return <SuccessView action={action} />;
  }

  if (action === "approve") {
    return (
      <AccountAccessLayout title="Approve Access Request">
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <Typography variant="body1" align="center">
              Choose the role this user should have in your account.
            </Typography>
          </Grid>
          <Grid item>
            <Select
              label="Role"
              value={selectedRole}
              editValue={setSelectedRole}
              options={ROLE_OPTIONS.map(({ value, label, description }) => ({
                value,
                label: `${label} — ${description}`
              }))}
            />
          </Grid>
          {error && (
            <Grid item>
              <Typography color="error" variant="body2" align="center">
                {error}
              </Typography>
            </Grid>
          )}
          <Grid item container justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              onClick={onConfirm}
              disabled={isLoading || !uid}
            >
              {isLoading ? <CircularProgress size={20} /> : "Approve Access"}
            </Button>
          </Grid>
        </Grid>
      </AccountAccessLayout>
    );
  }

  return (
    <AccountAccessLayout title="Decline Access Request">
      <Grid container direction="column" spacing={3}>
        <Grid item>
          <Typography variant="body1" align="center">
            If you decline, your name and email address will be shared with the
            requesting buyer as their point of contact for this vendor
            relationship.
          </Typography>
        </Grid>
        {error && (
          <Grid item>
            <Typography color="error" variant="body2" align="center">
              {error}
            </Typography>
          </Grid>
        )}
        <Grid item container justifyContent="center">
          <Button
            variant="contained"
            color="error"
            onClick={onConfirm}
            disabled={isLoading || !uid}
          >
            {isLoading ? <CircularProgress size={20} /> : "Decline Request"}
          </Button>
        </Grid>
      </Grid>
    </AccountAccessLayout>
  );
};

export default JoinRequest;
